import Helpers from '../modules/Helpers'
import XHRPromise from 'xhr-promise'

class GeoSearch {
    
    constructor(input, results, options) {
        this.defaultOptions = {
            minSearchLength: 3,
            noMatchTimeoutDelay: 1000,
            placeholderText: {
                focus: 'Enter place or cottage name...',
                blur: 'Where would you like to go?'
            }
        }

        this.input = input
        this.results = results
        this.activeSearch = null

        

        this.options = Object.assign(this.defaultOptions, options)
        this.selectActions = []
        this.invalidActions = []

        this.inputContentIsValid = true

        this.events()
    }

    on(event, callback) {
        switch (event) {
            case 'select':
                this.selectActions.push(callback)
                break;
            case 'invalid':
                this.invalidActions.push(callback)
        }
    }

    events() {

        new Helpers(this.input).eventListener((e) => {
            this.inputContentIsValid = false
            this.runSearch()
        }, 'search')

        new Helpers(this.input).eventListener((e) => {
            this.input.dispatchEvent(new CustomEvent('search'))
        }, 'input')

        new Helpers(this.input).eventListener((e) => {
            this.input.setAttribute('placeholder', this.options.placeholderText.focus)
        }, 'focus')

        new Helpers(this.input).eventListener((e) => {
            
            // clear the results box on blur - with a delay to ensure click events in the results box would already have fired
            setTimeout(() => {
                this.input.setAttribute('placeholder', this.options.placeholderText.blur)
                if (!this.inputContentIsValid) {
                    this.invalidActions.forEach((cb) => {
                        cb(this.input.value)
                    })   
                }
                this.clearResults()
            }, 300)
        }, 'blur')

        new Helpers(this.input).eventListener((e) => {
            switch (e.keyCode) {
                case 40:
                    e.preventDefault()
                    e.stopPropagation()
                    this.highlightNextResult()
                    return false;
                    break;
                case 38:
                    e.preventDefault()
                    e.stopPropagation()
                    this.highlightPrevResult()
                    return false;
                    break;
                case 13:
                    e.preventDefault()
                    e.stopPropagation()
                    this.selectResult()
                    return false;
                    break;
                case 9:
                    e.preventDefault()
                    e.stopPropagation()
                    this.selectResult()
                    return false;
                    break;
            }
            return true;
        }, 'keydown')

        new Helpers(this.searchResults).eventListener((e) => {
            this.clearHighlight()
        }, 'mouseover')

        new Helpers(this.results).eventListener((e) => {
            const result = new Helpers(e.target).getClosest('li')
            const index = Array.from(this.results.children).indexOf(result)
            this.highlightResult(index)
            this.selectResult()
        })
    }

    getHighlightedResult() {
        return this.results.querySelector('li[aria-current="true"]')
    }

    clearHighlight() {
        const results = this.results.querySelectorAll('li')
        new Helpers(results).setAttributes({ 'aria-current': false })
    }

    highlightResult(index) {
        this.clearHighlight()
        const result = this.results.children[index]
        result.setAttribute('aria-current', true)
    }

    highlightPrevResult() {
        const currentHighlight = this.getHighlightedResult()
        let newHighlight = null
        if (currentHighlight === null || currentHighlight.previousElementSibling === null) {
            newHighlight = this.results.lastElementChild
        } else {
            newHighlight = currentHighlight.previousElementSibling
        }
        const newIndex = Array.from(this.results.children).indexOf(newHighlight)

        this.highlightResult(newIndex)
        return this
    }

    highlightNextResult() {
        const currentHighlight = this.getHighlightedResult()
        let newHighlight = null
        if (currentHighlight === null || currentHighlight.nextElementSibling === null) {
            newHighlight = this.results.firstElementChild
        } else {
            newHighlight = currentHighlight.nextElementSibling
        }
        const newIndex = Array.from(this.results.children).indexOf(newHighlight)

        this.highlightResult(newIndex)
        return this
    }


    activeResultData() {
        const result = this.getHighlightedResult()
        if (result !== null) {
            return {
                type: result.getAttribute('data-type'),
                code: result.getAttribute('data-code'),
                text: result.getAttribute('data-value'),
                url: result.getAttribute('data-url')
            }
        } else {
            return null
        }
    }

    selectResult() {
        if (this.hasResults()) {
            this.inputContentIsValid = true
            this.selectActions.forEach((cb) => {
                const data = this.activeResultData()
                if (data !== null) {
                    cb(data)
                }
            })
        }
    }

    setValue(value) {
        this.inputContentIsValid = true
        this.input.value = value
    }

    clearResults() {
        this.results.innerHTML = ''
    }

    hasResults() {
        return this.results.querySelectorAll('li').length>0
    }
    
    createResultElement(data) {
        const el = document.createElement('li')
        el.setAttribute('class','geo-search-result')
        if (data.Place.length > 0) {
            el.innerHTML = `<em>${data.Name}</em><span>${data.Place}</span>`
        } else {
            el.innerHTML = `<em>${data.Name}</em>`
        }
        el.setAttribute('data-value', data.Name)
        el.setAttribute('data-type', data.Type)
        el.setAttribute('data-code', data.Code)
        el.setAttribute('data-point', data.Point.join(','))
        el.setAttribute('data-url', data.Url)

        return el
    }


    runSearch() {        
        if (this.activeSearch !== null) {
            this.activeSearch.getXHR().abort()
            this.activeSearch = null
        }

        // if search term is atleast three characters or a numeric value
        if (this.input.value.length >= this.options.minSearchLength || (this.input.value.length >= 1 && !isNaN(this.input.value))) {

            this.activeSearch = new XHRPromise()

            this.activeSearch.send({
                url: `/feeds/geoSearch.aspx?s=${this.input.value}`
            }).then((response) => {
                const resultCount = response.responseText.length
                // clear the results list
                this.clearResults()
                // build and apply html for each result
                response.responseText.forEach((r) => {
                    this.results.appendChild(this.createResultElement(r))
                })

                if (resultCount > 0) {
                    if (resultCount === 1) {
                        // we have one result, highlight it
                        this.highlightResult(0)
                    }
                } else {
                    // no results for this term, log it as a GA event (with some debounce logic)
                    clearTimeout(this.noMatchTimeout)
                    this.noMatchTimeout = setTimeout(() => {
                        window.dataLayerQueue = window.dataLayerQueue || []
                        window.dataLayerQueue.push({ 'event': 'resultsLocationSearch', 'searchTerm': this.input.value });
                    }, this.options.noMatchTimeoutDelay)

                }
                this.activeSearch = null
            }).catch((error) => {
                if (error.reason !== 'abort') {
                    console.log(error)
                }
            });
        } else {
            // clear the results list
            this.clearResults()
        }
    }

}

export default GeoSearch